/**
 * buildNewProposal — the PURE core of the Contracts "New proposal" create
 * flow. The modal collects raw form state (title, account, line items with
 * string-typed qty/price inputs); this helper validates every required
 * field, normalizes the line items, computes totals with the proposalDoc
 * money math, and returns a document input shaped for the EXISTING
 * `createDocument` path in `lib/db/documents`.
 *
 * Pure by design: records in, result out. No store, no DOM, no clock —
 * the caller owns persistence (and the modal owns error display).
 */

import {
  computeProposalTotals,
  type ProposalLineItem,
  type ProposalTotals,
} from './proposalDoc';

// ---------------------------------------------------------------------------
// Input / output shapes
// ---------------------------------------------------------------------------

/** One raw line-item row from the form — qty/price may still be strings. */
export interface NewProposalLineInput {
  description: string;
  qty: number | string;
  unitPrice: number | string;
}

export interface NewProposalInput {
  title: string;
  /** The selected account (required — a proposal addresses someone). */
  accountId: string;
  accountName: string;
  /** At least one priced row is required. */
  lineItems: NewProposalLineInput[];
  ownerName?: string;
  createdBy?: string;
  /**
   * Which document kind to create. Defaults to 'proposal' (the original
   * flow); the Contracts view's "New contract" action passes 'contract'
   * so the SAME form/validation/totals produce a contract-kind doc that
   * `createDocument` fills from the contract template.
   */
  kind?: NewDocKind;
  /**
   * Branded visual template for proposals (see crm/proposalTemplates —
   * aurora / onyx / blueprint). Passed through verbatim; when omitted the
   * store stamps the kind's legacy default id.
   */
  templateId?: string;
}

/** Document kinds this create flow can produce. */
export type NewDocKind = 'proposal' | 'contract';

/**
 * Ready-to-persist document input — structurally assignable to
 * `NewDocumentInput` in `lib/db/documents` (kept structural so this
 * module stays store-free and trivially testable).
 */
export interface NewProposalDocInput {
  kind: NewDocKind;
  title: string;
  entityType: 'account';
  entityId: string;
  entityName: string;
  /** Computed grand total — the doc's recorded value, never fabricated. */
  value: number;
  ownerName?: string;
  createdBy?: string;
  /** Selected branded template id (proposals) — see crm/proposalTemplates. */
  templateId?: string;
}

export type BuildNewProposalResult =
  | {
      ok: true;
      doc: NewProposalDocInput;
      lineItems: ProposalLineItem[];
      totals: ProposalTotals;
    }
  | { ok: false; errors: string[] };

// ---------------------------------------------------------------------------
// Validation + build
// ---------------------------------------------------------------------------

/** Coerce a form value to a finite number; NaN for blanks/garbage. */
function toNumber(v: number | string): number {
  if (typeof v === 'number') return v;
  const trimmed = v.trim();
  if (trimmed === '') return Number.NaN;
  return Number(trimmed);
}

/**
 * Validate raw New-proposal form state and build the persistable document
 * input. Collects EVERY error (the modal lists them all at once) instead
 * of failing fast. On success, `doc.value` carries the computed total so
 * the existing proposal renderer (`defaultLineItemsForDoc`) can derive a
 * pricing table for preview without inventing figures.
 */
export function buildNewProposal(input: NewProposalInput): BuildNewProposalResult {
  const errors: string[] = [];

  const title = input.title.trim();
  if (!title) errors.push('Title is required.');

  if (!input.accountId || !input.accountName.trim()) {
    errors.push(
      `Account is required — pick who this ${input.kind ?? 'proposal'} addresses.`,
    );
  }

  if (input.lineItems.length === 0) {
    errors.push('Add at least one line item.');
  }

  const lineItems: ProposalLineItem[] = [];
  input.lineItems.forEach((raw, i) => {
    const n = i + 1;
    const description = raw.description.trim();
    if (!description) errors.push(`Line ${n}: description is required.`);

    const qty = toNumber(raw.qty);
    if (!Number.isFinite(qty) || qty <= 0) {
      errors.push(`Line ${n}: quantity must be a positive number.`);
    }

    const unitPrice = toNumber(raw.unitPrice);
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      errors.push(`Line ${n}: unit price must be a non-negative number.`);
    }

    lineItems.push({ description, qty, unitPrice, cadence: 'one-time' });
  });

  if (errors.length > 0) return { ok: false, errors };

  const totals = computeProposalTotals(lineItems);
  return {
    ok: true,
    doc: {
      kind: input.kind ?? 'proposal',
      title,
      entityType: 'account',
      entityId: input.accountId,
      entityName: input.accountName.trim(),
      value: totals.total,
      ownerName: input.ownerName?.trim() || undefined,
      createdBy: input.createdBy,
      templateId: input.templateId,
    },
    lineItems,
    totals,
  };
}
